import { Text } from "../typography";
import type {
  TypographyVariant,
  TypographyTone,
} from "../typography/types";

interface TableTextCellProps {
  /**
   * Value to display - can be string, number, object, or null/undefined
   */
  value: string | number | object | null | undefined;
  /**
   * Typography variant (default: "body")
   */
  variant?: TypographyVariant;
  /**
   * Typography tone (default: "body")
   */
  tone?: TypographyTone;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Placeholder text when value is empty (default: "—")
   */
  placeholder?: string;
  /**
   * Extract value from nested object property (e.g., "name" for country.name)
   */
  extractFrom?: string;
  /**
   * Custom formatter function
   */
  formatter?: (value: string | number) => string;
  /**
   * Conditional tone function based on value
   */
  getTone?: (value: string | number) => TypographyTone;
}

/**
 * Helper to extract string value from object field
 */
const extractValue = (
  value: string | number | object | null | undefined,
  extractFrom?: string
): string | number | null => {
  if (value === null || value === undefined) {
    return null;
  }

  // If it's already a string or number, return it
  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  // If extractFrom is specified, try to get that property
  if (extractFrom && typeof value === "object" && extractFrom in value) {
    const extracted = (value as Record<string, unknown>)[extractFrom];
    if (extracted === null || extracted === undefined) {
      return null;
    }
    if (typeof extracted === "string" || typeof extracted === "number") {
      return extracted;
    }
    // If extracted value is an object with a "value" property
    if (typeof extracted === "object" && "value" in extracted) {
      const val = (extracted as { value?: unknown }).value;
      return val !== null && val !== undefined
        ? typeof val === "number"
          ? val
          : String(val)
        : null;
    }
    return String(extracted);
  }

  // If it's an object with a "value" property
  if (typeof value === "object" && "value" in value) {
    const val = (value as { value?: unknown }).value;
    if (val === null || val === undefined) {
      return null;
    }
    return typeof val === "number" ? val : String(val);
  }

  return null;
};

/**
 * Reusable table cell component for displaying text
 * Handles object value extraction, formatting, and conditional styling
 */
export function TableTextCell({
  value,
  variant = "body",
  tone = "body",
  className,
  placeholder = "—",
  extractFrom,
  formatter,
  getTone,
}: TableTextCellProps) {
  const extractedValue = extractValue(value, extractFrom);

  if (extractedValue === null) {
    return (
      <Text variant={variant} tone={tone} className={className}>
        {placeholder}
      </Text>
    );
  }

  const displayValue = formatter
    ? formatter(extractedValue)
    : typeof extractedValue === "number"
    ? extractedValue.toString()
    : extractedValue;

  const finalTone = getTone ? getTone(extractedValue) : tone;

  return (
    <Text variant={variant} tone={finalTone} className={className}>
      {displayValue}
    </Text>
  );
}
